import { useToast } from '@shared/components/toast';
import {
  OriginatorStatus,
  useUpdateOriginatorMutation,
} from '@shared/generated/graphql';
import { useGlossary } from '@shared/hooks/useGlossary';
import { Order } from '@shared/types/order';
import { isNil } from 'lodash';
import { ReactElement, useState } from 'react';
import { isHeldInTmsSandbox } from './isHeldInTmsSandbox';
import { NotLiveCustomerSendDialog } from './NotLiveCustomerSendDialog';

type ResolveShouldSend = (shouldSend: boolean) => void;

type UseNotLiveCustomerSendConfirmationResult = {
  confirmSend: () => Promise<boolean>;
  confirmationDialog: ReactElement;
};

export function useNotLiveCustomerSendConfirmation(
  order: Order | null
): UseNotLiveCustomerSendConfirmationResult {
  const [resolveShouldSend, setResolveShouldSend] =
    useState<ResolveShouldSend | null>(null);
  const [isMakingLive, setIsMakingLive] = useState(false);
  const [updateOriginator] = useUpdateOriginatorMutation();
  const { sendToast } = useToast();
  const { tms } = useGlossary();

  const confirmSend = (): Promise<boolean> => {
    if (isNil(order) || !isHeldInTmsSandbox(order)) {
      return Promise.resolve(true);
    }
    return new Promise((resolve) => setResolveShouldSend(() => resolve));
  };

  const finish = (shouldSend: boolean) => {
    resolveShouldSend?.(shouldSend);
    setResolveShouldSend(null);
  };

  const makeCustomerLive = async (): Promise<boolean> => {
    const originatorId = order?.originator.id;
    if (isNil(originatorId)) return false;

    const response = await updateOriginator({
      variables: {
        input: { id: originatorId, status: OriginatorStatus.PRODUCTION },
      },
    }).catch(() => null);
    return response?.data?.updateOriginator.success ?? false;
  };

  const makeLiveAndSend = async () => {
    setIsMakingLive(true);
    const isLive = await makeCustomerLive();
    setIsMakingLive(false);
    if (!isLive) {
      sendToast("Couldn't make the customer live", {
        variant: 'error',
        description: `The order was not sent to ${tms}.`,
        isDismissible: true,
      });
    }
    finish(isLive);
  };

  const confirmationDialog = (
    <NotLiveCustomerSendDialog
      isOpen={!isNil(resolveShouldSend)}
      customerName={order?.originator.name}
      tms={tms}
      isMakingLive={isMakingLive}
      onMakeLiveAndSend={() => void makeLiveAndSend()}
      onSendAsTest={() => finish(true)}
      onCancel={() => finish(false)}
    />
  );

  return { confirmSend, confirmationDialog };
}
